import React, { useEffect, useRef } from 'react';
import { Link } from 'gatsby';
import styled from 'styled-components';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { usePrefersReducedMotion } from '@hooks';
import { formatDate } from '@utils';

const StyledArticlesSection = styled.section`
  max-width: 1000px;

  .inner {
    display: flex;
    flex-direction: column;
  }

  .articles-grid {
    ${({ theme }) => theme.mixins.resetList};
    display: flex;
    flex-direction: column;
    gap: 15px;
    position: relative;
    margin-top: 50px;
  }

  .more-button {
    ${({ theme }) => theme.mixins.button};
    margin: 80px auto 0;
  }
`;

const StyledArticle = styled.li`
  position: relative;
  cursor: default;
  transition: var(--transition);

  @media (prefers-reduced-motion: no-preference) {
    &:hover,
    &:focus-within {
      .article-inner {
        transform: translateY(-7px);
      }
    }
  }

  a {
    position: relative;
    z-index: 1;
  }

  .article-inner {
    ${({ theme }) => theme.mixins.boxShadow};
    position: relative;
    width: 100%;
    padding: 2rem 1.75rem;
    border-radius: var(--border-radius);
    background-color: var(--light-navy);
    transition: var(--transition);
    display: flex;
    flex-direction: column;
  }

  .article-header {
    display: flex;
    align-items: center;
    margin-bottom: 15px;
    gap: 15px;

    .folder {
      color: var(--green);
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;

      svg {
        width: 40px;
        height: 40px;
        display: block;
      }
    }

    .article-title {
      margin: 0;
      color: var(--lightest-slate);
      font-size: var(--fz-xxl);
      flex-grow: 1;

      a {
        position: static;

        &:before {
          content: '';
          display: block;
          position: absolute;
          z-index: 0;
          width: 100%;
          height: 100%;
          top: 0;
          left: 0;
        }
      }
    }
  }

  .article-description {
    color: var(--light-slate);
    font-size: 17px;
    line-height: 1.5;
    margin-bottom: 20px;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    position: relative;

    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      right: 0;
      width: 50px;
      height: 1.5em;
      background: linear-gradient(to right, transparent, var(--light-navy));
    }

    @media (max-width: 768px) {
      -webkit-line-clamp: 2;
    }

    a {
      ${({ theme }) => theme.mixins.inlineLink};
    }
  }

  .article-footer {
    margin-top: auto;

    .article-date {
      color: var(--green);
      font-family: var(--font-mono);
      font-size: var(--fz-xxs);

      .tag {
        color: var(--green);
        font-size: var(--fz-xxs);

        &:not(:last-of-type) {
          margin-right: 15px;
        }
      }
    }
  }

  .article-tech-list {
    display: flex;
    flex-wrap: wrap;
    padding: 0;
    margin: 0;
    list-style: none;

    li {
      color: var(--green);
      font-family: var(--font-mono);
      font-size: var(--fz-xxs);
      line-height: 1.75;

      &:not(:last-of-type) {
        margin-right: 15px;
      }
    }
  }
`;

const Articles = ({ articles }) => {
  const revealTitle = useRef(null);
  const revealArchiveLink = useRef(null);
  const revealArticles = useRef([]);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    sr.reveal(revealTitle.current, srConfig());
    sr.reveal(revealArchiveLink.current, srConfig());
    revealArticles.current.forEach((ref, i) => sr.reveal(ref, srConfig(i * 100)));
  }, []);

  const GRID_LIMIT = 3;
  const firstThree = articles.slice(0, GRID_LIMIT);

  const articleInner = node => {
    const { frontmatter, fields, html } = node;
    const { title, tags, date } = frontmatter;
    const { slug } = fields;

    return (
      <div className="article-inner">
        <div className="article-header">
          <div className="folder">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              role="img"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1"
              strokeLinecap="round"
              strokeLinejoin="round">
              <title>Folder</title>
              <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path>
            </svg>
          </div>
          <h3 className="article-title">
            <Link to={slug}>{title}</Link>
          </h3>
        </div>

        <div
          className="article-description"
          dangerouslySetInnerHTML={{
            __html: html,
          }}
        />

        <div className="article-footer">
          <span className="article-date">
            {formatDate(date)}
            <span>&nbsp;&mdash;&nbsp;</span>
            {tags && tags.length > 0 && (
              <>
                {tags.map((tag, i) => (
                  <span key={i} className="tag">
                    #{tag.toLowerCase().replace(/\s+/g, '-')}
                  </span>
                ))}
              </>
            )}
          </span>
        </div>

        {false && tags && (
          <ul className="article-tech-list">
            {tags.map((tag, i) => (
              <li key={i}>#{tag.toLowerCase().replace(/\s+/g, '-')}</li>
            ))}
          </ul>
        )}
      </div>
    );
  };

  return (
    <StyledArticlesSection>
      <h2 ref={revealTitle}>Recent Articles</h2>

      <Link className="inline-link archive-link" to="/articles" ref={revealArchiveLink}>
        view all articles
      </Link>

      <ul className="articles-grid">
        {prefersReducedMotion ? (
          <>
            {firstThree &&
              firstThree.map((node, i) => (
                <StyledArticle key={i}>{articleInner(node)}</StyledArticle>
              ))}
          </>
        ) : (
          <>
            {firstThree &&
              firstThree.map((node, i) => (
                <StyledArticle
                  key={i}
                  ref={el => (revealArticles.current[i] = el)}
                  style={{
                    transitionDelay: `${i * 100}ms`,
                  }}>
                  {articleInner(node)}
                </StyledArticle>
              ))}
          </>
        )}
      </ul>
    </StyledArticlesSection>
  );
};

export default Articles;
